import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import DatePicker from '../../../components/ui/DatePicker';

import oneWayCityOptions from '../../../data/oneWayCityOptions';

const HeroSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    date: ''
  });
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [fromSearchTerm, setFromSearchTerm] = useState('');
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [showPickupDatePicker, setShowPickupDatePicker] = useState(false);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  const getCitiesForSearch = () => {
    return oneWayCityOptions || [];
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    if (!formData?.from || !formData?.to || !formData?.date) {
      alert('Please fill in all required fields: From, To, and Date');
      return;
    }
    
    // Navigate to new search results page with accurate pricing
    const fromCity = formData?.from?.toLowerCase()?.replace(/\s+/g, '-');
    const toCity = formData?.to?.toLowerCase()?.replace(/\s+/g, '-');
    
    navigate('/search-results', {
      state: {
        from: formData?.from,
        to: formData?.to,
        date: formData?.date,
        fromCity,
        toCity
      }
    });
  };

  const filteredFromCities = fromSearchTerm?.trim() ? getCitiesForSearch()?.filter(city =>
    city?.toLowerCase()?.includes(fromSearchTerm?.toLowerCase())
  ) : [];

  const filteredToCities = toSearchTerm?.trim() ? getCitiesForSearch()?.filter(city =>
    city?.toLowerCase()?.includes(toSearchTerm?.toLowerCase())
  ) : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef?.current && !fromDropdownRef?.current?.contains(event?.target)) {
        setShowFromDropdown(false);
      }
      if (toDropdownRef?.current && !toDropdownRef?.current?.contains(event?.target)) {
        setShowToDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative min-h-[500px] sm:min-h-[550px] md:min-h-[600px] lg:min-h-[650px] flex items-center justify-center overflow-hidden">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-500 to-orange-500">
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-3 sm:px-4 py-12 sm:py-16 md:py-20">
        {/* Search Form */}
        <div className="max-w-5xl mx-auto bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {/* From City */}
            <div className="relative" ref={fromDropdownRef}>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                From City
              </label>
              <div className="relative">
                <Icon name="MapPin" className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-teal-600" size={18} />
                <input
                  type="text"
                  value={fromSearchTerm}
                  onChange={(e) => {
                    const value = e?.target?.value;
                    setFromSearchTerm(value);
                    if (value?.trim()) {
                      setShowFromDropdown(true);
                    } else {
                      setShowFromDropdown(false);
                      setFormData(prev => ({ ...prev, from: '' }));
                    }
                  }}
                  onFocus={() => {
                    if (fromSearchTerm?.trim()) {
                      setShowFromDropdown(true);
                    }
                  }}
                  placeholder="Select pickup city"
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent touch-manipulation"
                />
              </div>
              {showFromDropdown && filteredFromCities?.length > 0 && (
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 sm:max-h-60 overflow-y-auto">
                  {filteredFromCities?.map((city, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        handleInputChange('from', city);
                        setFromSearchTerm(city);
                        setShowFromDropdown(false);
                      }}
                      className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base hover:bg-teal-50 transition-colors touch-manipulation"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* To City */}
            <div className="relative" ref={toDropdownRef}>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                To City
              </label>
              <div className="relative">
                <Icon name="MapPin" className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-orange-600" size={18} />
                <input
                  type="text"
                  value={toSearchTerm}
                  onChange={(e) => {
                    const value = e?.target?.value;
                    setToSearchTerm(value);
                    if (value?.trim()) {
                      setShowToDropdown(true);
                    } else {
                      setShowToDropdown(false);
                      setFormData(prev => ({ ...prev, to: '' }));
                    }
                  }}
                  onFocus={() => {
                    if (toSearchTerm?.trim()) {
                      setShowToDropdown(true);
                    }
                  }}
                  placeholder="Select destination city"
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation"
                />
              </div>
              {showToDropdown && filteredToCities?.length > 0 && (
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 sm:max-h-60 overflow-y-auto">
                  {filteredToCities?.map((city, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        handleInputChange('to', city);
                        setToSearchTerm(city);
                        setShowToDropdown(false);
                      }}
                      className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base hover:bg-orange-50 transition-colors touch-manipulation"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            <div className="relative sm:col-span-2 md:col-span-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Pickup Date
              </label>
              <div className="relative">
                <Icon name="Calendar" className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-teal-600" size={18} />
                <input
                  type="text"
                  value={formData?.date}
                  onClick={() => setShowPickupDatePicker(true)}
                  readOnly
                  placeholder="Select date"
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer touch-manipulation"
                />
              </div>
              {showPickupDatePicker && (
                <div className="absolute z-50 mt-2 left-0 sm:left-auto">
                  <DatePicker
                    isOpen={showPickupDatePicker}
                    onSelect={(date) => {
                      handleInputChange('date', date);
                      setShowPickupDatePicker(false);
                    }}
                    onClose={() => setShowPickupDatePicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Search Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleSearch}
              className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-4 text-sm sm:text-base bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 touch-manipulation"
            >
              <Icon name="search" className="mr-2" size={18} />
              Search Rides
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;